import { Fragment, useEffect, useMemo, useState } from 'react';
import { Loader2, RotateCcw, Save, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchSelect } from '@/components/ui/search-select';
import { RemoteSearchSelect } from '@/components/ui/remote-search-select';
import { migrationV1V2Api } from '../api/migration-v1-v2';
import type {
  EnCodeOption,
  MatiereNiveauAssociation,
  MatiereNiveauViewResponse,
} from '../api/migration-v1-v2';

const NONE_VALUE = '__none__';

interface EditState {
  enCode: string | null;
  /** Libellé de la matière choisie (affichage « nom (code) » hors MEF). */
  label?: string | null;
  dirty: boolean;
  source?: 'ai';
  confidence?: number | null;
}

/**
 * Codes BCN par ASSOCIATION matière × niveau (écoles EN).
 *
 * La nomenclature BCN est définie par MEF : une même discipline peut porter un
 * code différent selon le niveau. Cette section liste chaque association
 * (matière V1, niveau V1) avec un select contraint aux matières associables au
 * MEF du niveau (data API `mef_matieres`) — repli sur la recherche globale
 * quand le niveau ne porte pas de MEF (primaire). Pré-mapping IA par
 * association, enregistré en overrides école (type `matiere_niveau`).
 */
export function MatiereNiveauBcnSection({ schoolId }: { schoolId: number }) {
  const [data, setData] = useState<MatiereNiveauViewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [edits, setEdits] = useState<Record<string, EditState>>({});
  const [saving, setSaving] = useState(false);
  const [aiRunning, setAiRunning] = useState(false);
  const [aiCount, setAiCount] = useState(0);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const view = await migrationV1V2Api.getMatiereNiveauView(schoolId);
      setData(view);
      setEdits({});
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]);

  const associations = useMemo(() => data?.associations ?? [], [data]);

  /** Associations groupées par matière (l'ordre V1 est déjà matière puis niveau). */
  const groups = useMemo(() => {
    const byMatiere = new Map<string, MatiereNiveauAssociation[]>();
    for (const a of associations) {
      const list = byMatiere.get(a.matiere);
      if (list) list.push(a);
      else byMatiere.set(a.matiere, [a]);
    }
    return [...byMatiere.entries()].map(([matiere, rows]) => ({ matiere, rows }));
  }, [associations]);

  const editFor = (a: MatiereNiveauAssociation): EditState =>
    edits[a.key] ?? { enCode: a.enCode, label: a.enLabel ?? null, dirty: false };

  const setEdit = (a: MatiereNiveauAssociation, enCode: string | null, label?: string | null) => {
    setEdits((prev) => ({
      ...prev,
      [a.key]: { enCode, label: label ?? null, dirty: true },
    }));
  };

  const dirtyKeys = useMemo(
    () => associations.filter((a) => edits[a.key]?.dirty).map((a) => a.key),
    [associations, edits]
  );

  const runAi = async () => {
    setAiRunning(true);
    setAiCount(0);
    try {
      const resp = await migrationV1V2Api.aiSuggestMatiereNiveau(schoolId);
      if (!resp.enabled) return;
      const byKey = new Map(resp.suggestions.map((s) => [s.key, s]));
      let count = 0;
      setEdits((prev) => {
        const next = { ...prev };
        for (const a of associations) {
          // On ne pré-remplit que les associations sans code et non éditées.
          if (a.enCode || next[a.key]?.dirty) continue;
          const s = byKey.get(a.key);
          if (s?.enCode) {
            next[a.key] = { enCode: s.enCode, label: s.enLabel ?? null, dirty: true, source: 'ai', confidence: s.confidence };
            count++;
          }
        }
        return next;
      });
      setAiCount(count);
    } catch {
      /* best-effort */
    } finally {
      setAiRunning(false);
    }
  };

  const saveAll = async () => {
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const a of associations) {
        const e = edits[a.key];
        if (!e?.dirty) continue;
        if (!e.enCode) {
          await migrationV1V2Api.deleteReferentialOverride(schoolId, 'matiere_niveau', a.key);
        } else {
          await migrationV1V2Api.upsertReferentialOverride(schoolId, {
            type: 'matiere_niveau',
            sourceId: a.key,
            sourceLabel: `${a.matiere} × ${a.niveau}`,
            targetCode: null,
            targetLabel: null,
            enCode: e.enCode,
            // Contexte pour la remontée au global (insert-si-absent) : le choix
            // fait ici alimente la config globale et devient le défaut ailleurs.
            matiere: a.matiere,
            niveau: a.niveau,
            mef: a.mef,
          });
        }
      }
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const optionLabel = (o: EnCodeOption): string =>
    o.isLangueVivante
      ? `${o.label} · LV${o.rang ?? ''} (${o.code})`
      : `${o.label} (${o.code})`;

  /** Options du select recherchable d'une cellule : « aucun code » + options du
   *  MEF (matières + langues concrètes LV1/LV2/LV3), avec un texte de recherche
   *  enrichi (langue, rang). Réinjecte le code courant s'il n'est plus proposé. */
  const buildCellOptions = (
    options: EnCodeOption[],
    currentCode: string | null,
    currentLabel: string | null
  ) => {
    const opts = [
      { value: NONE_VALUE, label: 'Aucun code' },
      ...options.map((o) => ({
        value: o.code,
        label: optionLabel(o),
        searchText: [o.label, o.code, o.langueLibelle ?? '', o.isLangueVivante ? `LV${o.rang ?? ''}` : '']
          .filter(Boolean)
          .join(' '),
      })),
    ];
    if (currentCode && !options.some((o) => o.code === currentCode)) {
      opts.push({ value: currentCode, label: `${currentLabel ?? currentCode} (${currentCode})`, searchText: currentCode });
    }
    return opts;
  };

  const labelForCode = (mef: string | null, code: string | null): string | null => {
    if (!code) return null;
    if (mef && data?.mefOptions[mef]) {
      const opt = data.mefOptions[mef].find((o) => o.code === code);
      if (opt) return opt.label;
    }
    return null;
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" style={{ textTransform: 'none' }}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200" style={{ background: '#f8fafc' }}>
        <div>
          <div className="text-sm font-semibold text-gray-800">
            Codes BCN par association matière × niveau
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            La nomenclature BCN est définie par MEF : choisissez le code de chaque
            association. Quand le niveau porte un MEF, les choix sont limités aux
            matières officiellement associables ; sinon (primaire) la recherche est libre.
            À défaut de correspondance, le code de repli <strong>009900</strong> (« activités
            non spécialisées / divers ») est appliqué aux niveaux portant un MEF.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={runAi} disabled={aiRunning || loading}>
            {aiRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Pré-mapper (IA)
          </Button>
          <Button size="sm" onClick={saveAll} disabled={dirtyKeys.length === 0 || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Enregistrer{dirtyKeys.length > 0 ? ` (${dirtyKeys.length})` : ''}
          </Button>
        </div>
      </div>

      {aiCount > 0 && (
        <div className="px-4 py-2 text-xs border-b border-violet-100" style={{ background: '#f5f3ff', color: '#5b21b6' }}>
          <Sparkles className="inline h-3.5 w-3.5 mr-1 text-violet-500" />
          {aiCount} code{aiCount > 1 ? 's' : ''} BCN suggéré{aiCount > 1 ? 's' : ''} par l'IA (✨) — vérifiez puis Enregistrer.
        </div>
      )}
      {error && (
        <div className="px-4 py-2 text-xs border-b border-red-100" style={{ background: '#fef2f2', color: '#991b1b' }}>
          {error}
        </div>
      )}

      {loading && !data ? (
        <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Chargement des associations…
        </div>
      ) : (
        <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '38%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Matière V1', 'Niveau', 'MEF', 'Code BCN de l\'association', 'Origine'].map((h, i) => (
                <th
                  key={h}
                  className={`px-3 py-2 font-medium text-gray-700 border-b border-gray-200 ${i === 4 ? 'text-right' : 'text-left'}`}
                  style={{ textTransform: 'none', fontSize: '0.75rem', letterSpacing: 'normal' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.matiere}>
                {g.rows.map((a, idx) => {
                  const e = editFor(a);
                  const options = a.mef ? data?.mefOptions[a.mef] ?? [] : [];
                  // Libellé affiché : options MEF prioritaires, sinon libellé
                  // porté par l'édition / l'association (recherche libre).
                  const currentLabel = labelForCode(a.mef, e.enCode) ?? e.label ?? null;
                  return (
                    <tr key={a.key} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 align-middle">
                        {idx === 0 ? (
                          <span className="font-medium text-gray-800">{a.matiere}</span>
                        ) : (
                          <span className="text-gray-300 pl-2">〃</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700">{a.niveau}</td>
                      <td className="px-3 py-1.5">
                        {a.mef ? (
                          <code className="text-xs text-gray-600">{a.mef}</code>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        <div className="flex items-center gap-1.5">
                          {e.source === 'ai' && <Sparkles className="h-3.5 w-3.5 shrink-0 text-violet-500" />}
                          {a.mef && options.length > 0 ? (
                            <SearchSelect
                              value={e.enCode ?? NONE_VALUE}
                              onValueChange={(v) =>
                                setEdit(
                                  a,
                                  v === NONE_VALUE ? null : v,
                                  options.find((o) => o.code === v)?.label ?? null
                                )
                              }
                              options={buildCellOptions(options, e.enCode, currentLabel)}
                              placeholder="Choisir un code…"
                              searchPlaceholder="Rechercher (langue, matière, code)…"
                              className={`h-8 text-xs ${e.dirty ? 'border-violet-400' : ''}`}
                              contentClassName="min-w-[24rem]"
                            />
                          ) : (
                            <RemoteSearchSelect
                              value={e.enCode ?? ''}
                              selectedLabel={currentLabel}
                              onSelect={(code, label) => setEdit(a, code || null, label)}
                              search={async (q) =>
                                (await migrationV1V2Api.searchEnMatieres(q)).map((o) => ({
                                  code: o.code,
                                  label: optionLabel(o),
                                }))
                              }
                              placeholder="Rechercher un code BCN…"
                              className={e.dirty ? 'border-violet-400' : ''}
                            />
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        <div className="inline-flex items-center gap-1.5">
                          <span className="text-xs text-gray-500">
                            {e.dirty
                              ? 'Modifié'
                              : a.source === 'override'
                                ? 'École'
                                : a.source === 'global'
                                  ? 'Défaut global'
                                  : a.source === 'matiere'
                                    ? 'Hérité matière'
                                    : '—'}
                          </span>
                          {a.source === 'override' && !e.dirty && (
                            <button
                              type="button"
                              title="Retirer l'override (retour au défaut)"
                              className="text-gray-400 hover:text-gray-600"
                              onClick={() => setEdit(a, null)}
                            >
                              <RotateCcw className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </Fragment>
            ))}
            {associations.length === 0 && !loading && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-sm text-gray-400">
                  Aucune association matière × niveau trouvée dans la V1.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}
